/**
 * Centralized OIDC token storage.
 *
 * SECURITY NOTE: Tokens are stored in browser storage (localStorage/sessionStorage),
 * which is accessible to JavaScript and vulnerable to XSS. The ideal fix is httpOnly,
 * Secure, SameSite cookies set by the backend — requires server-side auth changes.
 * This module centralizes access so a future cookie migration can happen in one place.
 *
 * Mitigations applied here:
 * - Refresh tokens stored in sessionStorage (cleared when tab closes; not used by app today)
 * - Access/ID tokens remain in localStorage for session persistence across tabs
 */

const ACCESS_TOKEN_KEY = 'oidc_access_token';
const ID_TOKEN_KEY = 'oidc_id_token';
const REFRESH_TOKEN_KEY = 'oidc_refresh_token';
const USER_INFO_KEY = 'oidc_user_info';
const TOKEN_EXPIRES_AT_KEY = 'oidc_token_expires_at';

export interface StoredUserInfo {
  identityProvider: string;
  userId: string;
  userDetails: string;
  email?: string;
  userRoles: string[];
}

export function storeTokens(tokens: {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
}): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
  if (tokens.idToken) {
    localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);
  }
  if (tokens.refreshToken) {
    sessionStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
}

export function storeUserInfo(userInfo: StoredUserInfo): void {
  localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getIdToken(): string | null {
  return localStorage.getItem(ID_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return sessionStorage.getItem(REFRESH_TOKEN_KEY) ?? localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function getUserInfoRaw(): string | null {
  return localStorage.getItem(USER_INFO_KEY);
}

export function hasAuthTokens(): boolean {
  return !!(getAccessToken() && getUserInfoRaw());
}

export function storeTokenExpiresAt(expiresIn: number): void {
  const expiresAt = Math.floor(Date.now() / 1000) + expiresIn;
  localStorage.setItem(TOKEN_EXPIRES_AT_KEY, String(expiresAt));
}

export function getTokenExpiresAt(): number | null {
  const raw = localStorage.getItem(TOKEN_EXPIRES_AT_KEY);
  if (!raw) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

export function clearAuthTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(ID_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_INFO_KEY);
  localStorage.removeItem(TOKEN_EXPIRES_AT_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
}
